<template>
    <div
        :class="[
            'dropdown',
            props.shade === 'lighter' ? 'dropdown--lighter' : ''
        ]"
    >
        <div
            :class="[
                'dropdown__button',
                dropdownOpen ? 'dropdown__button--open' : '',
                props.buttonLabel ? 'dropdown__button--label' : '',
            ]"
            @click="toggleDropdown"
        >
            <i v-if="props.buttonIcon" :class="['pi', props.buttonIcon]"/>
            <div v-if="props.buttonLabel" class="dropdown__button--label__text">
                {{ props.buttonLabel }}
                <i v-if="dropdownOpen" class="pi pi-chevron-up"/>
                <i v-else class="pi pi-chevron-down"/>
            </div>
        </div>
        <div
            v-if="dropdownOpen"
            :class="[
                'dropdown__menu',
                'dropdown__menu--right',
                props.size === 'large' ? 'dropdown__menu--large' : '',
                props.menuItems.length > 7 ? 'dropdown__menu--scrollable' : '',
            ]"
        >
            <div
                v-for="(item, index) in props.menuItems"
                :key="index"
                :class="[
                    'dropdown__menu__item',
                    item.endOfSection ? 'dropdown__menu__item--end' : '',
                    item.onClick && !item.disabled ? 'dropdown__menu__item--clickable' : '',
                    item.disabled ? 'dropdown__menu__item--disabled' : '',
                ]"
                @click="clickMenuItem(item)"
            >
                <div class="dropdown__menu__item__row">
                    <div class="dropdown__menu__item__left">
                        <img
                            v-if="item.icon"
                            :src="item.icon"
                            :alt="item.name"
                            aria-hidden="true"
                        />
                        {{ item.name }}
                        <div
                            v-if="item.status"
                            :class="[
                                'dropdown__menu__item__status',
                                item.status === 'ready' ? 'dropdown__menu__item__status--ready' : '',
                                item.status === 'processing' ? 'dropdown__menu__item__status--processing' : '',
                            ]"
                        >
                            {{ item.status === 'ready' ? 'Ready' : item.status === 'processing' ? 'Processing' : 'Available'}}
                        </div>
                    </div>
                    <div
                        v-if="item.onToggle"
                        class="dropdown__menu__item__toggle"
                    >
                        <ToggleSwitch
                            v-model="item.checked"
                            :disabled="item.disabled"
                            @update:modelValue="toggleMenuItem(item)"
                        />
                    </div>
                    <div
                        v-if="props.selectedMenuItem !== undefined && props.selectedMenuItem === item.name"
                        class="dropdown__menu__item__selected"
                    >
                        <i class="pi pi-check"/>
                    </div>
                </div>
                <div
                    v-if="item.description"
                    class="dropdown__menu__item__description"
                >
                    {{ item.description }}
                </div>
                <div v-if="item.slotFunctionality" @click.stop>
                    <slot :name="getSlotName(item.name)"></slot>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, getCurrentInstance } from 'vue';
import { MenuItem } from '../types/dropdown-interface';

const props = defineProps<{
    buttonLabel?: string;
    buttonIcon?: string;
    shade?: 'normal' | 'lighter';
    size?: 'small' | 'large';
    menuItems: MenuItem[];
    selectedMenuItem?: string;
    group?: string;
    dropdownOpenByParent?: boolean;
}>();

const dropdownOpen = ref(props.dropdownOpenByParent || false);
const componentId = getCurrentInstance()?.uid;

const toggleDropdown = () => {
    dropdownOpen.value = !dropdownOpen.value;

    if (dropdownOpen.value) {
        document.dispatchEvent(new CustomEvent('closeOtherDropdowns', {
            detail: {
                except: componentId,
                group: props.group
            }
        }));
    }
};

const handleCloseOthers = (event: any) => {
    if (event.detail?.group === props.group && event.detail?.except !== componentId) {
        dropdownOpen.value = false;
    }
}

const clickMenuItem = (item: MenuItem) => {
    if (item.onClick && !item.disabled) {
        item.onClick();
        dropdownOpen.value = false;
    }
}

const toggleMenuItem = (item: MenuItem) => {
    if (item.onToggle) {
        item.onToggle();
    }
}

const getSlotName = (name: string) => {
    return name.replace(/\s+/g, '').toLowerCase();
}

onMounted(() => {
    document.addEventListener('closeOtherDropdowns', handleCloseOthers);
});

onUnmounted(() => {
    document.removeEventListener('closeOtherDropdowns', handleCloseOthers);
});
</script>

<style scoped lang="scss">
.dropdown {
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
    font-size: 1.4rem;

    &:hover {
        border-radius: 0.8rem 0.8rem 0 0;
        cursor: pointer;
    }

    &__button {
        height: 3.6rem;
        aspect-ratio: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 0.8rem;

        &:hover {
            background-color: map-get($colours, accent-purple-3);
        }

        &--open {
            background-color: map-get($colours, accent-purple-3);
        }

        &--label {
            aspect-ratio: unset;
            border-radius: 3.2rem;
            margin: 0.4rem 0;
            height: inherit;
            padding: 0.4rem;

            &__text {
                display: flex;
                align-items: center;
                gap: 0.4rem;
                padding: 0 0.8rem;
                white-space: nowrap;

                i {
                    font-size: 1.2rem;
                }
            }
        }

        i {
            font-size: 1.6rem;
        }
    }

    &__menu {
        display: flex;
        flex-direction: column;
        position: absolute;
        bottom: 0;
        left: 0;
        transform: translateY(100%);
        border-radius: 0.8rem;
        z-index: 10001;
        padding: 1.2rem;
        font-family: $font-secondary;
        font-size: 1.4rem;
        background: map-get($colours, accent-purple-3);
        min-width: 21.5rem;
        box-shadow: 0 2rem 4rem 0 rgba(0, 0, 0, 0.1);

        &--right {
            right: 0;
            left: initial;
        }

        &--large {
            width: 31rem;
        }

        &--scrollable {
            max-height: 35rem;
            overflow-y: auto;
        }

        &__item {
            border-radius: 0.8rem;
            padding: 0.8rem;
            position: relative;

            &--end {
                border-bottom: 1px solid rgba(255, 255, 255, 0.05);
                margin-bottom: 0.8rem;
                padding-bottom: 1.6rem;
            }

            &--clickable:hover {
                background-color: map-get($colours, accent-purple-2);
            }

            &--disabled {
                cursor: not-allowed;
                opacity: 0.5;
            }

            &__row {
                display: flex;
                align-items: center;
                justify-content: space-between;
                flex-wrap: wrap;
                cursor: pointer;
            }

            &__left {
                display: flex;
                align-items: center;
                gap: 0.8rem;

                img {
                    width: 1.6rem;
                    height: 1.6rem;
                }
            }

            &__status {
                font-size: 1.2rem;
                background-color: #fff3c8;
                color: black;
                padding: 0.1rem 0.6rem;
                border-radius: 0.8rem;

                &--ready {
                    background-color: #2a5136;
                    color: white;
                }

                &--processing {
                    background-color: #bfdbfe;
                    color: black;
                }
            }

            &__selected i {
                font-weight: bold;
                font-size: 1.6rem;
            }

            &__description {
                font-size: 1.2rem;
                color: rgba(255, 255, 255, 0.5);
                padding-top: 0.4rem;
                line-height: 1.4;
                white-space: normal;
            }
        }
    }

    &--lighter {
        .dropdown__button {
            background-color: map-get($colours, accent-purple-2);

            &:hover {
                background-color: map-get($colours, accent-purple-1);
            }

            &--open {
                background-color: map-get($colours, accent-purple-1);
            }
        }

        .dropdown__menu {
            background: map-get($colours, accent-purple-2);

            &__item--clickable:hover {
                background-color: map-get($colours, accent-purple-3);
            }
        }
    }
}
</style>
